import React, { useEffect, useState, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Modal from 'react-modal'
import { styled, css, ThemeProvider, Box } from '@mui/material'
import { getStateEligiblity } from '@helpers/extendedServiceContract'
import InfoModal from '@shared/info-modal'
import { setStockMessage as reduxSetStockMessage } from '@redux/modules/product'
import { graphql, StaticQuery } from 'gatsby'

import StrapiHtml from '@templates/strapi-cms/content-types/HTML'
import { getCurrentLocation, getRegionZone } from '@helpers/geo-location'
import { productPrice, getRequiredAddon, availabilityStockMessage, getWarrantyDataByRegionZone } from '@helpers/product'
import { addToCart, getCartItemQuantity, cartProduct } from '@helpers/cart'
import { addToDataLayer_nextgen, analyticsProduct } from '@helpers/google-tag-manager'
import { fetchPromotions } from '@services/checkout'
import { HOME_PAGE, PLP } from '@constants/page-names'
import { fetchProductBySku, fetchProductWarehouseAvailability } from '@services/product'
import { AddonModelPropType } from '@models/api/products/addon-model'
import WarrantyModalTemplate from '@shared/modals/warranty-modal'
import useCartVersionChecker from '@hooks/useCartVersionChecker'
import useCartV2 from '@hooks/useCartV2'
import AddToCartStockMessage from './add-to-cart-stock-message'
import AddToCartRequiredAddons from './add-to-cart-required-addons'
import AddToCartModalProducts from './add-to-cart-modal-products'
import AddToCartAddons from './add-to-cart-addons'
import AddToCartCheckmark from './add-to-cart-checkmark'
import AddToCartModalButtons from './add-to-cart-modal-buttons'
import CloseCartModal from '../../shared/modals/close-cart-modal'
import YouMayAlsoLikeWrapper from './you-may-also-like-wrapper'
import { MUI_THEME } from '../../../@rtg2022'

if (process.env.NODE_ENV !== 'test') Modal.setAppElement('#___gatsby')

const ScrollContainer = styled('div')(
  () => css`
    height: auto;
    width: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    .AddToCartCheckmark,
    .add-to-cart-stock-message {
      width: 100%;
    }
  `,
)

const ModalContent = styled('div')(
  // CSS ported from add-to-cart-modal.scss
  ({ theme }) => css`
    background: #ffffff;
    padding-top: 2em;
    padding: 1em;
    padding-bottom: 128px; // space below the CTA butttons fixed on mobile
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    top: 0;
    overflow-x: hidden;
    overflow-y: auto;

    hr {
      margin: 1.5em 0;
    }
    ${theme.breakpoints.down('sm')} {
      max-width: 730px;
    }

    ${theme.breakpoints.up('md')} {
      padding: 1em 2em;
      padding-bottom: 0;
      max-height: 85vh;
      max-width: 838px;
      box-shadow: 0px 0px 0px 1px #e0e0e0;
      border-radius: 2px;
      right: auto;
      bottom: auto;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      #you-may-also-like__cart,
      #you-may-also-like__loading__cart {
        transition: 1s all cubic-bezier(0.4, 0, 0.2, 1);
      }
      .modal-scroll-content {
        width: 769px;
      }
    }
  `,
)

const StyleModalActions = styled('div')(
  css`
    width: 100%;
  `,
)

function AddToCartModal({ ...props }) {
  const [reachedCartLimit, setReachedCartLimit] = useState(false)
  const [reqConfirmed, setReqConfirmed] = useState(false)
  const [discount, setDiscount] = useState(0)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [showWarrantyModal, setShowWarrantyModal] = useState(false)
  const [isWarrantyEnabled, setIsWarrantyEnabled] = useState(props.warrantyEnabled || false)
  const [stockMessage, setStockMessage] = useState(props.stockMessage)
  const modalScrollContent = useRef()
  const [modalHeight, setModalHeight] = useState(0)

  const location = getCurrentLocation()
  const { addCartLineItemsMutation } = useCartV2()
  const cartVersion = useCartVersionChecker()

  const allRequiredAddon = () => {
    const { addons, requiredSelected, activeAddons } = props
    const { region } = getRegionZone()
    if (addons && (requiredSelected === null || typeof requiredSelected === 'undefined')) {
      const requiredAddons = addons.filter(
        addon => addon.addon_required && addon.catalog_availability && addon.catalog_availability[region],
      )
      return requiredAddons.length > 0 && !activeAddons.includes(requiredAddons[0]) && requiredAddons
    }
    return null
  }
  const requiredAddons = allRequiredAddon()
  const includesRequiredAddon = requiredAddons?.length > 0
  const [requiredSelected, setRequiredSelected] = useState(props.requiredSelected)
  const [allActiveAddons, setAllActiveAddons] = useState(null)
  const [reqAddedToCart, setReqAddedToCart] = useState(false)

  useEffect(() => {
    // read modal height to dynamically place YMAL slider arrows
    if (!modalScrollContent.current) return
    // eslint-disable-next-line no-undef
    const resizeObserver = new ResizeObserver(() => {
      setModalHeight(modalScrollContent?.current?.clientHeight)
    })
    resizeObserver.observe(modalScrollContent?.current)
    // eslint-disable-next-line consistent-return
    return () => resizeObserver.disconnect()
  })

  useEffect(() => {
    const { pageName = '', product, onSetStockMessage } = props
    if (!cartVersion) return
    if (!stockMessage && product.sku !== '83333333') {
      if (
        location &&
        product &&
        (location.region !== 'OOM' ||
          (location?.region === 'OOM' && [HOME_PAGE, PLP].includes(pageName)) ||
          ((product.delivery_type === 'O' || product.delivery_type === 'U') && location.region === 'OOM'))
      ) {
        fetchProductWarehouseAvailability(product.sku, location.distribution_index, location.state)
          .then(({ data }) => {
            const availStockMessage = availabilityStockMessage({
              availableOn: data.earliestAvailability,
              product,
              rtg_location: location,
              setStockMessage: onSetStockMessage,
              useDeprecatedStockMessage: true,
            })
            setStockMessage(availStockMessage)
            if (stockCheck(availStockMessage, true) && !includesRequiredAddon) {
              addToCartAttempt()
            }
          })
          .catch(() => {
            onSetStockMessage('Out of Stock ')
          })
      } else if (product.delivery_type !== 'O' && product.delivery_type !== 'U' && location.region === 'OOM') {
        // TODO: This catch and call to onSetStoreMessage does not work on PLP page
        onSetStockMessage('Not available in your region')
      } else {
        onSetStockMessage(stockMessage)
        if (stockCheck(stockMessage, true) && !includesRequiredAddon) {
          addToCartAttempt()
        }
      }
    } else {
      onSetStockMessage(stockMessage)
      if (stockCheck(stockMessage) && !includesRequiredAddon) {
        addToCartAttempt()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartVersion])

  const stockCheck = (message, bypassRegion) =>
    bypassRegion
      ? message !== 'Discontinued ' && message !== 'Available Soon ' && message !== 'Out of Stock '
      : message !== 'Not available in your region ' &&
        message !== 'Discontinued ' &&
        message !== 'Available Soon ' &&
        message !== 'Out of Stock '

  const onAddRequiredAddon = requiredAddon => {
    const { activeAddons } = props
    addToCartAttempt(
      requiredSelected && [...activeAddons, ...requiredAddons.filter(addon => requiredAddon.skus.includes(addon.sku))],
    )
    setAllActiveAddons(requiredSelected && allActiveAddons)
    setReqAddedToCart(true)
  }

  const checkForSliderTitle = (product, source) => {
    let sliderTitle = ''
    if (product && product.sliderTitle) sliderTitle = `${product.sliderTitle}`
    else if (source) sliderTitle = `${source.replace(/[-_]/g, ' ')}`
    return sliderTitle
  }

  const addToCartAttempt = useCallback(
    async (requiredAllActiveAddons = null) => {
      const {
        product,
        index,
        quantity,
        activeAddons,
        promoItems,
        promoDescription,
        promoQualifierQuantity,
        promoTargetQuantity,
        source,
        foundationSelection,
      } = props

      let newActiveAddons = activeAddons

      // check if warranty is enabled by props or state and send this
      if (requiredAllActiveAddons) {
        newActiveAddons = requiredAllActiveAddons
      }

      const cartItemQuantity = getCartItemQuantity(product.sku)
      if (cartItemQuantity === 10) {
        setReachedCartLimit(true)
      } else {
        let finalActiveAddons = null
        if (newActiveAddons && newActiveAddons.length > 0) {
          finalActiveAddons = newActiveAddons
        }
        let reducedActiveAddons = []
        if (finalActiveAddons && finalActiveAddons.length > 0) {
          for (let i = 0, n = finalActiveAddons.length; i < n; i++) {
            reducedActiveAddons.push(cartProduct(finalActiveAddons[i]))
          }
        } else {
          reducedActiveAddons = null
        }
        if (promoItems && promoTargetQuantity && promoQualifierQuantity) {
          const requestItems = [
            {
              sku: product.sku,
              quantity: parseInt(promoQualifierQuantity),
            },
          ]
          promoItems.map(item =>
            requestItems.push({
              sku: item.sku,
              quantity: parseInt(promoTargetQuantity),
            }),
          )
          const { region, zone } = getRegionZone()
          fetchPromotions({
            requestItems,
            region,
            zone,
          }).then(data => {
            setDiscount(data.totalSavings || 0)
            for (let x = 0; x < promoQualifierQuantity; x++) {
              addToCart(
                {
                  product,
                  price: productPrice(product),
                  activeAddons: reducedActiveAddons,
                  warrantyEnabled: isWarrantyEnabled,
                },
                foundationSelection,
                true,
              )
            }
            for (let i = 0, n = promoItems.length; i < n; i++) {
              for (let x = 0; x < promoTargetQuantity; x++) {
                const promotion = data.lineItems.find(
                  item => item.sku === promoItems[i].sku && item.quantity && 'bonusPrice' in item,
                )
                const price = promotion?.bonusPrice + promotion?.bonusItemSavings || 0

                addToCart(
                  {
                    product: { ...promoItems[i], price },
                    price,
                    activeAddons: null,
                    isWarrantyEnabled,
                    promotion,
                  },
                  foundationSelection,
                )
              }

              fetchProductBySku(promoItems[0].sku)
                .then(result => {
                  addToDataLayer_nextgen('ee_add', {
                    ecommerce: {
                      add: {
                        position: index,
                        promoDescription,
                        list: checkForSliderTitle(product, source),
                        products: [
                          analyticsProduct({ ...product, stockMessage }, promoTargetQuantity, null, isWarrantyEnabled),
                          analyticsProduct({ ...result, stockMessage }, promoTargetQuantity, null, isWarrantyEnabled),
                        ],
                      },
                    },
                  })
                })
                .catch(() => {
                  addToDataLayer_nextgen('ee_add', {
                    ecommerce: {
                      add: {
                        position: index,
                        promoDescription,
                        list: checkForSliderTitle(product, source),
                        products: [
                          analyticsProduct(product, promoTargetQuantity, null, isWarrantyEnabled),
                          promoItems[0],
                        ],
                      },
                    },
                  })
                })
            }
          })
        } else {
          if (cartVersion === 'v1') {
            addToCart(
              {
                product,
                price: productPrice(product),
                activeAddons: reducedActiveAddons,
                warrantyEnabled: isWarrantyEnabled,
                quantity,
              },
              foundationSelection,
              true,
            )
          }
          if (cartVersion === 'v2') {
            addCartLineItemsMutation.mutate([
              {
                sku: product.sku,
                quantity: quantity || 1,
                warrantyEnabled: isWarrantyEnabled,
                isConfigurable: product?.isConfigurable || false,
                ...(product?.price ? { unitPrice: product?.price } : {}),
                ...(product?.additionalOptions ? { additionalOptions: product.additionalOptions } : {}),
              },
            ])
          }

          fetchProductWarehouseAvailability(product.sku, location.distribution_index, location.state)
            .then(({ data }) => {
              const availStockMessage = availabilityStockMessage({
                availableOn: data.earliestAvailability,
                product,
                rtg_location: location,
                useDeprecatedStockMessage: true,
              })

              if (requiredAllActiveAddons) setReqAddedToCart(true)
              product.active_addons = finalActiveAddons
                ? finalActiveAddons.map(p => analyticsProduct(p, p.quantity || 1), null, isWarrantyEnabled)
                : []

              addToDataLayer_nextgen('ee_add', {
                ecommerce: {
                  add: {
                    position: index,
                    list: checkForSliderTitle(product, source),
                    products: [
                      analyticsProduct(
                        { ...product, stockMessage: availStockMessage },
                        quantity,
                        null,
                        isWarrantyEnabled,
                      ),
                    ],
                  },
                },
              })
            })
            .catch(() => {
              console.error('Out of Stock ')
            })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isWarrantyEnabled, location, props, stockMessage],
  )

  const onChangeRequiredAddons = value => {
    setRequiredSelected(value)
    setReqConfirmed(true)
  }

  const showInfoModalChange = () => setShowInfoModal(true)

  const toggleWarrantyEnabled = value => {
    setTimeout(() => {
      setIsWarrantyEnabled(value)
    }, 0)
  }

  const shouldShowWarrantyModal = () => {
    setShowWarrantyModal(true)
  }

  const closeWarrantyModal = () => {
    setShowWarrantyModal(false)
  }

  const closeBunkieModal = () => {
    setShowInfoModal(false)
  }

  const {
    modalOpen,
    product,
    closeModal,
    activeAddons,
    promoDescription,
    promoItems,
    promoQualifierQuantity,
    promoTargetQuantity,
    promoStrikethroughPrice,
    foundationSelection,
  } = props

  const requiredAddon = getRequiredAddon(requiredAddons)
  const isStateEligible = getStateEligiblity()

  const regionZoneData = getRegionZone()

  const warrantyData = product.warranty_pricing
    ? getWarrantyDataByRegionZone(product.warranty_pricing, regionZoneData)
    : product.warrantyPrice
  const warrantyInfo = {
    isWarrantyEnabled,
    warrantyData,
  }
  const WarrantyModal = warrantyData ? WarrantyModalTemplate : null

  return (
    <ThemeProvider theme={MUI_THEME}>
      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        className="reset-default-styles"
        overlayClassName="modal-overlay"
        // eslint-disable-next-line jsx-a11y/aria-role
        role=""
        ariaModal={false}
      >
        <ModalContent
          data-testid="modal-content"
          role="dialog"
          aria-modal="true"
          aria-label="Add To Cart Modal"
          aria-labelledby="add-to-cart-header"
        >
          <CloseCartModal closeModal={closeModal} />
          <div>
            <ScrollContainer className="modal-scroll-content" ref={modalScrollContent}>
              {(!includesRequiredAddon || reqAddedToCart) && !reachedCartLimit && stockCheck(stockMessage) && (
                <AddToCartCheckmark />
              )}
              {includesRequiredAddon && !reqAddedToCart && !reachedCartLimit && stockCheck(stockMessage) && (
                <h3 className="unable black">Please accept or decline required add-ons.</h3>
              )}
              <AddToCartStockMessage
                includesRequiredAddon={includesRequiredAddon}
                reachedCartLimit={reachedCartLimit}
                stockMessage={stockMessage}
              />
              <Box sx={{ width: '100%' }}>
                {product && (
                  <AddToCartModalProducts
                    warrantyInfo={warrantyInfo}
                    product={product}
                    promoQualifierQuantity={promoQualifierQuantity}
                    reachedCartLimit={reachedCartLimit}
                    stockMessage={stockMessage}
                    includesRequiredAddon={includesRequiredAddon}
                    toggleWarrantyEnabled={toggleWarrantyEnabled}
                    showWarrantyModal={shouldShowWarrantyModal}
                    addToCart={addToCart}
                    isStateEligible={isStateEligible}
                    foundationSelection={foundationSelection}
                    style={{ width: '100% !important' }}
                    promoAddedToCart={promoItems && promoItems.length > 0}
                    selectedPromo={promoItems && promoItems[0]}
                  >
                    {includesRequiredAddon && !reqAddedToCart && requiredAddon && (
                      <AddToCartRequiredAddons
                        requiredAddon={requiredAddon}
                        requiredSelected={requiredSelected}
                        onChangeRequiredAddons={onChangeRequiredAddons}
                        showInfoModalChange={showInfoModalChange}
                      />
                    )}
                    {((allActiveAddons && allActiveAddons.length > 0) || (activeAddons && activeAddons.length > 0)) && (
                      <AddToCartAddons allActiveAddons={allActiveAddons} activeAddons={activeAddons} />
                    )}
                    {/* TODO: Check business if this promos still apply ? or all falls into bonus buy  */}
                    {/* {promoItems && promoItems.length > 0 && (
                      <AddToCartPromoAddons
                        promoItems={promoItems}
                        promoStrikethroughPrice={promoStrikethroughPrice}
                        shouldShowStrikethroughPrice={product?.strikethrough?.[getRegionZone().region] || false}
                        promoTargetQuantity={promoTargetQuantity}
                        discount={discount}
                      />
                    )} */}
                    <StyleModalActions>
                      <AddToCartModalButtons
                        closeModal={closeModal}
                        includesRequiredAddon={includesRequiredAddon}
                        reqAddedToCart={reqAddedToCart}
                        reqConfirmed={reqConfirmed}
                        onAddRequiredAddon={onAddRequiredAddon}
                        requiredAddon={requiredAddon}
                        stockMessage={stockMessage}
                        isWarrantyEnabled={isWarrantyEnabled}
                        product={product}
                      />
                    </StyleModalActions>
                  </AddToCartModalProducts>
                )}
              </Box>
              <YouMayAlsoLikeWrapper modalHeight={modalHeight} product={product} />
            </ScrollContainer>
          </div>
        </ModalContent>
        {showInfoModal && (
          <InfoModal
            label="What is a Bunkie Board?"
            btnClass="bunkie-board"
            mdlClass="rtg-bunkie-board-modal"
            shouldShowModal={showInfoModal}
            closeModal={closeBunkieModal}
          >
            <StaticQuery
              query={graphql`
                query ModalInfoQuery2 {
                  strapiHtml(Title: { eq: "HTML - Bunkie Board Modal" }) {
                    ...StrapiHtmlFragment
                  }
                }
              `}
              render={data => <StrapiHtml data={data.strapiHtml} />}
            />
          </InfoModal>
        )}
      </Modal>
      {WarrantyModal && (
        <WarrantyModal
          shouldShowModal={showWarrantyModal}
          closeModal={closeWarrantyModal}
          region={warrantyData.provider_name}
        />
      )}
    </ThemeProvider>
  )
}

const mapStateToProps = state => ({ ...state.cart })

const mapDispatchToProps = dispatch => ({
  onSetStockMessage: message => dispatch(reduxSetStockMessage(message)),
})

AddToCartModal.propTypes = {
  pageName: PropTypes.string,
  product: PropTypes.object,
  index: PropTypes.number,
  activeAddons: PropTypes.arrayOf(AddonModelPropType),
  promoItems: PropTypes.array,
  promoDescription: PropTypes.string,
  promoQualifierQuantity: PropTypes.number,
  promoTargetQuantity: PropTypes.number,
  warrantyEnabled: PropTypes.bool,
  modalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  promoStrikethroughPrice: PropTypes.number,
  stockMessage: PropTypes.any,
  onSetStockMessage: PropTypes.func,
  requiredSelected: PropTypes.arrayOf(AddonModelPropType),
  addons: PropTypes.array,
  source: PropTypes.string,
  quantity: PropTypes.bool,
  foundationSelection: PropTypes.objectOf(PropTypes.any),
}

// eslint-disable-next-line prettier/prettier
export default connect(mapStateToProps, mapDispatchToProps)(AddToCartModal)
